import type {
  CreateInterviewInput,
  CreateJobInput,
  HiringDecision,
  Interview,
  InterviewNote,
  InterviewPreview,
  InterviewReport,
  InterviewSession,
  InterviewSummary,
  Job,
  ResumeBrief,
  ResumeBriefRequest,
  UpdateJobInput,
  Voice,
} from '@ai-interview/shared';
import { getConfig } from './config';
import { SESSION_ENDED_EVENT, getFreshSession, refreshSession, signOut } from './auth';

/**
 * Typed client for the backend. Recruiter calls carry the Cognito ID token;
 * the candidate call is the one endpoint that does not, because candidates have
 * no account.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function sessionEnded(): ApiError {
  window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
  return new ApiError(401, 'Your session has expired. Please sign in again.');
}

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown; authenticated?: boolean } = {},
  retried = false,
): Promise<T> {
  const { apiBaseUrl } = getConfig();
  const headers: Record<string, string> = {};

  if (init.body !== undefined) headers['content-type'] = 'application/json';

  if (init.authenticated !== false) {
    const session = await getFreshSession();
    if (!session) throw sessionEnded();
    // API Gateway's JWT authorizer is configured against the user pool client,
    // so it wants the ID token rather than the access token.
    headers['authorization'] = `Bearer ${session.idToken}`;
  }

  const response = await fetch(`${apiBaseUrl.replace(/\/+$/, '')}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (response.status === 401 && init.authenticated !== false) {
    // Rejected although it had not expired by this machine's clock - a clock
    // running behind, say. One refresh and retry covers that; failing again
    // means the session really is over.
    if (!retried && (await refreshSession())) return request<T>(path, init, true);
    signOut();
    throw sessionEnded();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload['error'] === 'string' ? payload['error'] : `Request failed (${response.status})`,
      payload['details'],
    );
  }
  return payload as T;
}

/** One page of a newest-first list, and where the next page starts. */
export interface Page<T> {
  items: T[];
  cursor?: string;
}

function pageQuery(cursor?: string): string {
  return cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
}

export const api = {
  listJobs: (cursor?: string) => request<Page<Job>>(`/jobs${pageQuery(cursor)}`),

  getJob: (jobId: string) => request<Job>(`/jobs/${jobId}`),

  createJob: (input: CreateJobInput) => request<Job>('/jobs', { method: 'POST', body: input }),

  updateJob: (jobId: string, input: UpdateJobInput) =>
    request<Job>(`/jobs/${jobId}`, { method: 'PATCH', body: input }),

  deleteJob: (jobId: string) => request<void>(`/jobs/${jobId}`, { method: 'DELETE' }),

  /** Voices on the ElevenLabs account, for the role form. */
  listVoices: () => request<{ items: Voice[] }>('/voices'),

  listInterviews: (jobId: string, cursor?: string) =>
    request<Page<InterviewSummary>>(`/jobs/${jobId}/interviews${pageQuery(cursor)}`),

  /**
   * Turns a resume into a brief for the recruiter to review. Stateless: the
   * file is read and discarded server-side; only an invite stores the brief.
   */
  resumeBrief: (jobId: string, input: ResumeBriefRequest) =>
    request<ResumeBrief>(`/jobs/${jobId}/resume-brief`, { method: 'POST', body: input }),

  inviteCandidate: (jobId: string, input: CreateInterviewInput) =>
    request<{ interview: Interview; inviteUrl: string }>(`/jobs/${jobId}/interviews`, {
      method: 'POST',
      body: input,
    }),

  getInterview: (interviewId: string) => request<InterviewReport>(`/interviews/${interviewId}`),

  /** Records the recruiter's decision; `null` clears it. */
  decide: (interviewId: string, outcome: HiringDecision | null) =>
    request<{ interview: Interview }>(`/interviews/${interviewId}/decision`, {
      method: 'POST',
      body: { outcome },
    }),

  addNote: (interviewId: string, text: string) =>
    request<InterviewNote>(`/interviews/${interviewId}/notes`, { method: 'POST', body: { text } }),

  /**
   * A new link for the same candidate row. Every earlier link stops working,
   * and like the first one this is shown once.
   */
  reissueInvite: (interviewId: string, expiresInHours: number) =>
    request<{ interview: Interview; inviteUrl: string }>(`/interviews/${interviewId}/reissue`, {
      method: 'POST',
      body: { expiresInHours },
    }),

  /** Withdraws a link that has not been used; it stops working at once. */
  revokeInvite: (interviewId: string) =>
    request<{ interview: Interview }>(`/interviews/${interviewId}/revoke`, { method: 'POST' }),

  rescore: (interviewId: string) =>
    request<{ interviewId: string; status: string }>(`/interviews/${interviewId}/rescore`, {
      method: 'POST',
    }),

  /**
   * Candidate-facing, and a read: describes the interview without consuming the
   * single attempt, so opening or refreshing the link is free.
   */
  previewInterview: (interviewToken: string) =>
    request<InterviewPreview>('/public/preview', {
      method: 'POST',
      body: { interviewToken },
      authenticated: false,
    }),

  /** Spends the invite: mints the agent and the conversation token. */
  startSession: (interviewToken: string) =>
    request<InterviewSession>('/public/session', {
      method: 'POST',
      body: { interviewToken },
      authenticated: false,
    }),
};
